import { useId } from 'react';
import type { TableDefinition, Vec2 } from '../engine/tables/definition';
import { rampDirectionMarkers } from '../render2d/tableGeometry';

function path(points: Vec2[]): string {
  return points.map((p, i) => `${i ? 'L' : 'M'} ${p.x} ${p.y}`).join(' ');
}
function offset(points: Vec2[], distance: number): Vec2[] {
  return points.map((p, i) => {
    const a = points[Math.max(0, i - 1)]!;
    const b = points[Math.min(points.length - 1, i + 1)]!;
    const length = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    return {
      x: p.x - ((b.y - a.y) / length) * distance,
      y: p.y + ((b.x - a.x) / length) * distance,
    };
  });
}

/** Small, authored motifs sit under real hardware rather than faking a shot. */
function FieldMotif({ table }: { table: TableDefinition }) {
  const { width: w, height: h } = table.board;
  const accent = table.theme.accentColor;
  const secondary = table.theme.secondaryColor ?? accent;
  const motif = table.theme.machineArtId ?? table.id;
  if (motif === 'candy-carnival')
    return (
      <g opacity=".15" stroke={secondary} fill="none" strokeWidth="1.2">
        {[0.28, 0.52, 0.72].map((x, i) => (
          <g
            key={x}
            transform={`translate(${w * x} ${h * (0.46 + i * 0.05)}) rotate(${i * 25 - 30})`}
          >
            <ellipse rx="4" ry="2" />
            <path d="M -4 -1 L -7 -3 L -7 3 L -4 1 M 4 -1 L 7 -3 L 7 3 L 4 1" />
          </g>
        ))}
      </g>
    );
  if (motif === 'cosmic-relay')
    return (
      <g opacity=".2" fill="none" stroke={secondary} strokeWidth=".3">
        {[10, 17, 24].map((r) => (
          <ellipse
            key={r}
            cx={w * 0.48}
            cy={h * 0.3}
            rx={r}
            ry={r * 0.45}
            transform={`rotate(-25 ${w * 0.48} ${h * 0.3})`}
          />
        ))}
        {Array.from({ length: 24 }, (_, i) => (
          <circle
            key={i}
            cx={w * (0.1 + ((i * 31) % 80) / 100)}
            cy={h * (0.1 + ((i * 17) % 52) / 100)}
            r=".3"
            fill={accent}
          />
        ))}
      </g>
    );
  if (motif === 'haunted-keep')
    return (
      <g opacity=".12" fill="none" stroke={secondary} strokeWidth=".7">
        {[0.28, 0.5, 0.72].map((x) => (
          <path
            key={x}
            d={`M ${w * x - 5} ${h * 0.52} V ${h * 0.36} Q ${w * x} ${h * 0.29} ${w * x + 5} ${h * 0.36} V ${h * 0.52} Z`}
          />
        ))}
      </g>
    );
  if (motif === 'jungle-temple')
    return (
      <g opacity=".15" fill={secondary} stroke={accent} strokeWidth=".3">
        {Array.from({ length: 9 }, (_, i) => (
          <ellipse
            key={i}
            cx={w * (i % 2 ? 0.79 : 0.17)}
            cy={h * (0.18 + i * 0.055)}
            rx="2.2"
            ry="5"
            transform={`rotate(${i % 2 ? 35 : -35} ${w * (i % 2 ? 0.79 : 0.17)} ${h * (0.18 + i * 0.055)})`}
          />
        ))}
      </g>
    );
  if (motif === 'deep-sea-dive')
    return (
      <g opacity=".2" fill="none" stroke={secondary} strokeWidth=".4">
        {[0.22, 0.35, 0.48, 0.61].map((y) => (
          <path
            key={y}
            d={`M ${w * 0.08} ${h * y} Q ${w * 0.3} ${h * y - 5} ${w * 0.5} ${h * y} T ${w * 0.88} ${h * y}`}
          />
        ))}
        {[5, 10, 15].map((r) => (
          <circle key={r} cx={w * 0.47} cy={h * 0.53} r={r} />
        ))}
      </g>
    );
  if (motif === 'clockwork-district')
    return (
      <g opacity=".2" fill="none" stroke={accent} strokeWidth=".6">
        {[
          { x: 0.27, y: 0.3, r: 8 },
          { x: 0.62, y: 0.49, r: 11 },
        ].map(({ x, y, r }) => (
          <g key={x} transform={`translate(${w * x} ${h * y})`}>
            <circle r={r} />
            <circle r={r * 0.7} />
            <circle r="2" />
            {Array.from({ length: 12 }, (_, i) => (
              <path key={i} d={`M ${r - 1} 0 H ${r + 1.8}`} transform={`rotate(${i * 30})`} />
            ))}
          </g>
        ))}
      </g>
    );
  if (motif === 'frontier-run')
    return (
      <g opacity=".16" fill={secondary} stroke={accent} strokeWidth=".4">
        <path
          d={`M ${w * 0.1} ${h * 0.55} L ${w * 0.24} ${h * 0.42} H ${w * 0.34} L ${w * 0.42} ${h * 0.55} L ${w * 0.6} ${h * 0.44} H ${w * 0.71} L ${w * 0.83} ${h * 0.55} Z`}
        />
        <path
          fill="none"
          d={`M ${w * 0.26} ${h * 0.58} V ${h * 0.7} M ${w * 0.32} ${h * 0.58} V ${h * 0.7}`}
        />
        {Array.from({ length: 8 }, (_, i) => (
          <path key={i} d={`M ${w * 0.24} ${h * (0.59 + i * 0.015)} H ${w * 0.34}`} />
        ))}
      </g>
    );
  if (motif === 'dragon-keep')
    return (
      <g opacity=".15" fill={accent} stroke={secondary} strokeWidth=".4">
        <path
          d={`M ${w * 0.49} ${h * 0.54} L ${w * 0.31} ${h * 0.37} L ${w * 0.12} ${h * 0.47} L ${w * 0.18} ${h * 0.3} L ${w * 0.41} ${h * 0.36} L ${w * 0.5} ${h * 0.29} L ${w * 0.59} ${h * 0.36} L ${w * 0.82} ${h * 0.3} L ${w * 0.88} ${h * 0.47} L ${w * 0.69} ${h * 0.37} Z`}
        />
      </g>
    );
  if (motif === 'neon-rush')
    return (
      <g opacity=".12" stroke={accent} strokeWidth="1.1">
        {Array.from({ length: 7 }, (_, i) => (
          <path key={i} d={`M ${w * 0.15 + i * 2} ${h * 0.63} L ${w * 0.64 + i * 2} ${h * 0.38}`} />
        ))}
      </g>
    );
  return (
    <g opacity=".18" fill={secondary}>
      {Array.from({ length: 18 }, (_, i) => (
        <rect
          key={i}
          x={w * 0.22 + (i % 6) * w * 0.09}
          y={h * 0.49 + Math.floor(i / 6) * 2.5}
          width={w * 0.09}
          height="2.5"
          opacity={(i + Math.floor(i / 6)) % 2 ? 0 : 1}
        />
      ))}
    </g>
  );
}

export default function TablePreview({
  table,
  className,
}: {
  table: TableDefinition;
  className?: string;
}) {
  const prefix = useId().replace(/:/g, '');
  const { width: w, height: h } = table.board;
  const accent = table.theme.accentColor;
  const secondary = table.theme.secondaryColor ?? '#9fcbd2';
  const field = table.theme.playfieldColor ?? '#101a28';
  const metal = `url(#${prefix}-metal)`;
  const ball = `url(#${prefix}-ball)`;
  const ramps = table.parts.filter((p) => p.kind === 'ramp');
  return (
    <svg
      className={className}
      viewBox={`-3 -3 ${w + 6} ${h + 8}`}
      role="img"
      aria-label={`${table.name} playfield preview`}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id={`${prefix}-field`} x1="0" y1="0" x2=".8" y2="1">
          <stop stopColor="#18222c" />
          <stop offset=".3" stopColor={field} />
          <stop offset="1" stopColor="#070e15" />
        </linearGradient>
        <linearGradient id={`${prefix}-metal`} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#e8eff1" />
          <stop offset=".35" stopColor="#b5c4ce" />
          <stop offset=".65" stopColor="#526571" />
          <stop offset="1" stopColor="#d6e2e7" />
        </linearGradient>
        <radialGradient id={`${prefix}-ball`} cx=".3" cy=".25" r=".7">
          <stop stopColor="white" />
          <stop offset=".28" stopColor="#e5edf0" />
          <stop offset=".65" stopColor="#788b98" />
          <stop offset="1" stopColor="#192a38" />
        </radialGradient>
        <clipPath id={`${prefix}-clip`}>
          <rect x="2" y="2" width={w - 4} height={h - 4} rx="2" />
        </clipPath>
      </defs>
      <rect x="-1" y="1" width={w + 2} height={h + 2} rx="3.5" fill="#05090e" />
      <rect x="0" y="0" width={w} height={h} rx="3" fill={metal} />
      <rect
        x="1.4"
        y="1.4"
        width={w - 2.8}
        height={h - 2.8}
        rx="2"
        fill={`url(#${prefix}-field)`}
      />
      <g clipPath={`url(#${prefix}-clip)`}>
        <FieldMotif table={table} />
        {ramps.map((r) => {
          const spacing = (r.width ?? 4.6) * 0.3;
          return (
            <g key={r.id} fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path
                d={path(r.path)}
                stroke={r.color ?? secondary}
                strokeWidth={r.width ?? 4.6}
                opacity=".12"
              />
              {r.transport === 'orbit' ? (
                <path
                  d={path(r.path)}
                  stroke={secondary}
                  strokeWidth=".55"
                  opacity=".65"
                  strokeDasharray="1.2 1"
                />
              ) : (
                [-1, 1].map((side) => (
                  <g key={side}>
                    <path
                      d={path(offset(r.path, side * spacing))}
                      stroke="#030b12"
                      strokeWidth="1.1"
                    />
                    <path
                      d={path(offset(r.path, side * spacing))}
                      stroke={metal}
                      strokeWidth=".42"
                    />
                  </g>
                ))
              )}
              {rampDirectionMarkers(r.path).map((marker, index) => (
                <path
                  key={index}
                  d="M -.7 -.9 L .65 0 L -.7 .9"
                  transform={`translate(${marker.x} ${marker.y}) rotate(${(marker.angle * 180) / Math.PI})`}
                  stroke="#e1eef3"
                  strokeWidth=".28"
                  opacity=".7"
                />
              ))}
              <circle cx={r.entry.x} cy={r.entry.y} r="1.7" stroke={accent} strokeWidth=".4" />
            </g>
          );
        })}
        {table.parts.map((part) => {
          const color = part.color ?? accent;
          if (part.kind === 'wall' || part.kind === 'guide-rail')
            return (
              <path
                key={part.id}
                d={path(part.points)}
                fill="none"
                stroke={metal}
                strokeWidth={part.thickness}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={part.kind === 'guide-rail' && part.collider === false ? 0.3 : 0.9}
              />
            );
          if (part.kind === 'slingshot')
            return (
              <g key={part.id}>
                <path
                  d={`${path(part.points)} Z`}
                  fill={color}
                  fillOpacity=".3"
                  stroke="#dae8ed"
                  strokeWidth=".35"
                />
                <path d={path(part.points)} fill="none" stroke={color} strokeWidth=".55" />
              </g>
            );
          if (part.kind === 'bumper')
            return (
              <g key={part.id}>
                <circle
                  cx={part.center.x + 0.4}
                  cy={part.center.y + 0.7}
                  r={part.radius + 0.35}
                  fill="#040b12"
                />
                <circle
                  cx={part.center.x}
                  cy={part.center.y}
                  r={part.radius}
                  fill={metal}
                  stroke="#e6dace"
                  strokeWidth=".45"
                />
                <circle cx={part.center.x} cy={part.center.y} r={part.radius * 0.75} fill={color} />
                <circle cx={part.center.x} cy={part.center.y} r={part.radius * 0.48} fill={metal} />
                <circle
                  cx={part.center.x - part.radius * 0.25}
                  cy={part.center.y - part.radius * 0.25}
                  r={part.radius * 0.18}
                  fill="white"
                  opacity=".7"
                />
              </g>
            );
          if (part.kind === 'kicker')
            return (
              <g key={part.id}>
                <circle cx={part.center.x} cy={part.center.y} r={part.radius ?? 3} fill={metal} />
                <circle
                  cx={part.center.x}
                  cy={part.center.y}
                  r={(part.radius ?? 3) * 0.72}
                  fill="#050b12"
                />
                <circle
                  cx={part.center.x}
                  cy={part.center.y}
                  r={(part.radius ?? 3) + 0.6}
                  fill="none"
                  stroke={color}
                  strokeWidth=".3"
                />
              </g>
            );
          if (part.kind === 'target')
            return (
              <g key={part.id} opacity={part.appearsAfterPartId ? 0.45 : 1}>
                <rect
                  x={part.center.x - part.width / 2 - 0.3}
                  y={part.center.y - 0.5}
                  width={part.width + 0.6}
                  height="1.6"
                  fill="#040b12"
                />
                <rect
                  x={part.center.x - part.width / 2}
                  y={part.center.y - 0.8}
                  width={part.width}
                  height="1.3"
                  rx=".2"
                  fill={metal}
                />
                <rect
                  x={part.center.x - part.width * 0.35}
                  y={part.center.y - 0.6}
                  width={part.width * 0.7}
                  height=".4"
                  fill={color}
                />
              </g>
            );
          if (part.kind === 'insert')
            return (
              <path
                key={part.id}
                d="M 0 -1.6 L 1 1.6 L 0 .6 L -1 1.6 Z"
                transform={`translate(${part.center.x} ${part.center.y}) rotate(${part.rotationDeg ?? 0})`}
                fill={color}
                opacity=".65"
              />
            );
          if (part.kind === 'rollover' || part.kind === 'post')
            return (
              <circle
                key={part.id}
                cx={part.center.x}
                cy={part.center.y}
                r={Math.max(0.6, part.radius * 0.6)}
                fill={part.kind === 'post' ? metal : '#070d16'}
                stroke={color}
                strokeWidth=".35"
              />
            );
          if (part.kind === 'flipper-left' || part.kind === 'flipper-right') {
            const angle =
              ((part.kind === 'flipper-left' ? part.restAngleDeg : 180 - part.restAngleDeg) *
                Math.PI) /
              180;
            const end = {
              x: part.pivot.x + Math.cos(angle) * part.length,
              y: part.pivot.y + Math.sin(angle) * part.length,
            };
            return (
              <g key={part.id}>
                <line
                  x1={part.pivot.x}
                  y1={part.pivot.y + 0.4}
                  x2={end.x}
                  y2={end.y + 0.4}
                  stroke="#030b12"
                  strokeWidth="2.8"
                  strokeLinecap="round"
                />
                <line
                  x1={part.pivot.x}
                  y1={part.pivot.y}
                  x2={end.x}
                  y2={end.y}
                  stroke="#f7e5d7"
                  strokeWidth="2.3"
                  strokeLinecap="round"
                />
                <line
                  x1={part.pivot.x}
                  y1={part.pivot.y}
                  x2={end.x}
                  y2={end.y}
                  stroke={table.theme.flipperColor ?? accent}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <circle cx={part.pivot.x} cy={part.pivot.y} r=".65" fill={metal} />
              </g>
            );
          }
          if (part.kind === 'plunger-lane') {
            const rest = part.restPosition ?? {
              x: part.bounds.x + part.bounds.width / 2,
              y: part.bounds.y + part.bounds.height - 4,
            };
            return (
              <g key={part.id}>
                <path
                  d={path(part.divider?.points ?? [])}
                  fill="none"
                  stroke={metal}
                  strokeWidth=".6"
                />
                <line
                  x1={rest.x}
                  y1={rest.y + 1}
                  x2={rest.x}
                  y2={h - 2}
                  stroke={metal}
                  strokeWidth=".75"
                />
                <circle cx={rest.x} cy={rest.y} r="1.35" fill={ball} />
              </g>
            );
          }
          if (part.kind === 'drain')
            return (
              <line
                key={part.id}
                x1={part.center.x - part.width / 2}
                y1={part.center.y}
                x2={part.center.x + part.width / 2}
                y2={part.center.y}
                stroke="#02070c"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            );
          return null;
        })}
      </g>
      <path
        d={`M 1 ${h * 0.95} L ${w * 0.28} ${h - 1} H ${w * 0.72} L ${w - 1} ${h * 0.95} V ${h + 1} H 1 Z`}
        fill="#121c23"
        stroke="#53626c"
        strokeWidth=".3"
      />
      <path d={`M ${w * 0.35} ${h * 0.98} H ${w * 0.65}`} stroke={accent} strokeWidth=".45" />
      {[
        { x: 1.5, y: 1.5 },
        { x: w - 1.5, y: 1.5 },
        { x: 1.5, y: h - 1.5 },
        { x: w - 1.5, y: h - 1.5 },
      ].map(({ x, y }, i) => (
        <g key={i}>
          <circle cx={x} cy={y} r=".35" fill="#50606d" />
          <path d={`M ${x - 0.22} ${y} H ${x + 0.22}`} stroke="#d2dde2" strokeWidth=".1" />
        </g>
      ))}
    </svg>
  );
}
